/* eslint-disable security/detect-object-injection */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {
  useDocReport,
  useDocReportId,
  useExportView,
} from '../../../../hooks/reporting/useReporting';
import {
  ReportActionMenuOptionTypes,
  SourceTypeCode,
  SourceTypeContextCode,
  ToastType,
} from '../../../../shared/constants';
import {
  getMetaId,
  getNonMetaIds,
  getResultLabelIds,
  handleDocReport,
  handleExportView,
  handleReportDocId,
} from '../../../../utils/reportUtils';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import {useNavigate} from 'react-router-dom';
import {clearSelectedAction} from '../../../../redux/slice/actionButtonSlice';
import ArrDocMaxLimitWarning from '../../../common/arr-doc-max-limit-warning/ArrDocMaxLimitWarning';
import {clearDocViewState} from '../../../document-viewer/docViewerUtils';
import {
  storeDocumentListMasterId,
  storeSourceId,
  storeSourceTypeCode,
} from '../../../../redux/slice/docViewSlice';
import {useConfigKeyValue} from '../../../../hooks/categorization/useCategorization';
import {useDocumentViewerListId} from '../../../../hooks/document/useDocument';
import {DocumentViewerListIdRequest} from '../../../../api/types/document.type';
import {createDocumentViewerListRequestPayloadFromTally} from './TallyActionsUtils';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ActionMenuWrapper from '../../../action-menu/action-menu-wrapper/ActionMenuWrapper';
import {
  setActiveActionContext,
  updateTally,
} from '../../../../redux/slice/tallyReportSlice';
import _ from 'lodash';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../../redux/slice/docListSlice';
import {t} from 'i18next';

interface TallyActionsProps {
  actions: any;
  isDisabled: boolean;
  sourceType: SourceTypeCode;
  dataTestId: string;
  direction: any;
  subMenuDirection?: any;
  sourceTypeContext: SourceTypeContextCode;
  size?: any;
  onActionClick?: () => void;
}

const TallyActions: React.FC<TallyActionsProps> = ({
  actions,
  isDisabled = true,
  sourceType,
  dataTestId,
  direction,
  subMenuDirection,
  sourceTypeContext,
  size = 'small',
  onActionClick,
}) => {
  const selectedSource = useSelector(
    (state: RootState) => state.tallyreport.selectedSource
  );
  const tallyData = useSelector(
    (state: RootState) => state.tallyreport.tallyData
  );
  const subtallyData = useSelector(
    (state: RootState) => state.tallyreport.subtallyData
  );
  const tallyResponse = useSelector(
    (state: RootState) => state.tallyreport.tallyResponse
  );
  const subtallyResponse = useSelector(
    (state: RootState) => state.tallyreport.subtallyResponse
  );
  const tallyResult = useSelector(
    (state: RootState) => state.tallyreport.tallyResult
  );
  const subtallyResult = useSelector(
    (state: RootState) => state.tallyreport.subtallyResult
  );
  const tallyExportData = useSelector(
    (state: RootState) => state.tallyreport.tallyExportData
  );
  const subtallyExportData = useSelector(
    (state: RootState) => state.tallyreport.subtallyExportData
  );

  const actionMenuSelected = useSelector(
    (state: RootState) => state.actionButton.selectedActionMenu
  );
  const {mutate: fetchDocumentViewerListId, isPending: isDocViewLoading} =
    useDocumentViewerListId();
  const {mutateAsync: getReportDocId} = useDocReportId();
  const {mutateAsync: getExportView} = useExportView();
  const {mutateAsync: getDocReport} = useDocReport();
  const [showDocLimitDialog, setShowDocLimitDialog] = useState<boolean>(false);

  const [docLimitSourceTypeCode, setDocLimitSourceTypeCode] =
    useState<SourceTypeCode>();

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showLoaderOverlay, setShowLoaderOverlay] = useState<boolean>(false);

  const [actionConfig, setActionConfig] = useState<any>();
  const {refetch: fetchConfig} = useConfigKeyValue(
    actionConfig?.destinationType === 'DOCVIEW'
      ? SourceTypeCode.DOCVIEW
      : SourceTypeCode.DOCLIST
  );

  const triggerToast = useCallback((message: string, status: ToastType[]) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  }, []);

  const totalSelectedTallyBarDocs = tallyResult?.docs.reduce(
    (sum: number, item: {value: any}) => sum + (Number(item.value) || 0),
    0
  );

  const totalSelectedSubTallyDocs = subtallyResult?.docs.reduce(
    (sum: number, item: {value: any}) => sum + (Number(item.value) || 0),
    0
  );
  const requiredData = useMemo(
    () => ({
      SourceData: selectedSource,
      TallyData: tallyData,
      SubtallyData: subtallyData,
      TallyResponse: tallyResponse,
      SubtallyResponse: subtallyResponse,
      TallyResults: tallyResult,
      SubtallyResults: subtallyResult,
      TallyExportData: tallyExportData,
      subtallyExportData: subtallyExportData,
    }),
    [
      selectedSource,
      tallyData,
      tallyResponse,
      subtallyData,
      subtallyResponse,
      tallyResult,
      subtallyResult,
      tallyExportData,
      subtallyExportData,
    ]
  );

  const ACTION_CONFIG = {
    exportdata: {
      destinationType: 'EXPORTDATA',
      actionName: 'EXPORTDATA',
      tallyByText: 'DocView',
      isDocumentCount: false,
    },
    viewindocview: {
      destinationType: 'DOCVIEW',
      actionName: `${sourceType}TODOCVIEW`,
      tallyByText: 'Docview',
      isDocumentCount: true,
    },
    viewindocviewinnewtab: {
      destinationType: 'DOCVIEW',
      actionName: `${sourceType}TODOCVIEW`,
      tallyByText: 'Docview',
      isDocumentCount: true,
    },
    viewindoclist: {
      destinationType: 'DOCLIST',
      actionName: `${sourceType}TODOCLIST`,
      tallyByText: 'DocList',
      isDocumentCount: true,
    },
  };

  const activeActionContext = useSelector(
    (state: RootState) => state.tallyreport.activeActionContext
  );

  useEffect(() => {
    dispatch(setActiveActionContext(sourceType));
  }, []);

  useEffect(() => {
    if (!actionMenuSelected?.value) return;

    if (activeActionContext !== sourceType) return;

    const action = String(actionMenuSelected?.value || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '');

    switch (action) {
      case 'exportdata':
        handleExport(action, ACTION_CONFIG[action]);
        break;
      case 'viewindocview':
      case 'viewindocviewinnewtab':
        setActionConfig(ACTION_CONFIG[action]);
        setDocLimitSourceTypeCode(SourceTypeCode.DOCVIEW);
        setShowDocLimitDialog(true);
        break;
      case 'viewindoclist':
        setActionConfig(ACTION_CONFIG[action]);
        setDocLimitSourceTypeCode(SourceTypeCode.DOCLIST);
        setShowDocLimitDialog(true);
        break;
      case 'bulktag':
        // logic for 'Bulk Tag' goes here
        break;
      case 'bulkfolder':
        // logic for 'Bulk Folder' goes here
        break;
      case 'bulkassign':
        // logic for 'Bulk Assign' goes here
        break;
      default:
        break;
    }
  }, [actionMenuSelected, activeActionContext, sourceTypeContext]);

  const sourceId = requiredData?.SourceData?.[0]?.source?.[0];
  const sourceDetails = useMemo(() => {
    if (!selectedSource?.length || !tallyData || !tallyResult) return {};
    const tallyLabels = Array.from(
      new Set(
        (tallyResult.docs ?? []).map((d: any) => d?.label).filter(Boolean)
      )
    );

    const tallyInClause =
      tallyLabels.length > 0
        ? `IN(${tallyLabels.map((l) => `N'${l}'`).join(',')})`
        : null;

    const tally = {
      TallyByID: tallyData.type === 'Metadata' ? tallyData.id : '',
      TallyBy: null,
      DocListID: 0,
      TallyByCriteriaList: null,
      FilterByMetaData: [],
      FilterBySelection: [
        {
          FieldID: tallyData.type === 'Metadata' ? tallyData.id : '',
          LiteralValue: tallyInClause,
          FieldName: 'Docview',
          FieldType: tallyData.type,
        },
      ],
      IsBulkAction: true,
      TallyByType: tallyData.type,
      TallyByIDExceptMetadata: getNonMetaIds(requiredData?.TallyData) ?? '',
      SubTallyByID: null,
      SubTallyByIDExceptMetadata: null,
      SubTallyByType: null,
      TallyResultIDExceptMetadata:
        getResultLabelIds(
          requiredData?.TallyData?.idLabels,
          requiredData?.TallyResults?.docs
        ) ?? '',
      TallyResultID: null,
      SubTallyByResultIDExceptMetadata: null,
      TallyFilterValue: [],
      SubTallyFilterValue: [],
    };
    const buildSubTallyFilterBySelection = (
      docs: Array<{label?: string; header?: string}>
    ) => {
      const map: Record<string, Set<string>> = {};
      for (const doc of docs) {
        if (doc.label === undefined || doc.label === null) continue;
        if (!doc.header) continue;

        if (!map[doc.label]) {
          map[doc.label] = new Set<string>();
        }

        map[doc.label].add(doc.header.toUpperCase());
      }

      return Object.keys(map).map((label) => ({
        FieldID: null,
        FieldName: null,
        LiteralValue: `[TALLYBY]=N'${label}' AND [SUBTALLYBY] IN (${Array.from(
          map[label]
        )
          .map((h) => `'${h}'`)
          .join(',')})`,
        FieldType: null,
      }));
    };
    const filterBySelection = buildSubTallyFilterBySelection(
      subtallyResult?.docs || []
    );

    const subTally =
      sourceTypeContext === 'TALLYNOC' || !subtallyData
        ? {
            TallyByID: null,
            TallyBy: null,
            DocListID: 0,
            TallyByCriteriaList: null,
            FilterByMetaData: [],
            FilterBySelection: [],
            IsBulkAction: false,
            TallyByType: null,
            TallyByIDExceptMetadata: null,
            SubTallyByID: null,
            SubTallyByIDExceptMetadata: null,
            SubTallyByType: null,
            TallyResultIDExceptMetadata: null,
            TallyResultID: null,
            SubTallyByResultIDExceptMetadata: null,
            TallyFilterValue: [],
            SubTallyFilterValue: [],
          }
        : {
            TallyByID: getMetaId(requiredData?.SubtallyData) ?? '',
            TallyBy: requiredData?.SubtallyData?.type,
            DocListID: 0,
            TallyByCriteriaList: null,
            FilterByMetaData: [],
            FilterBySelection: filterBySelection,
            IsBulkAction: false,
            TallyByType: requiredData?.SubtallyData?.type,
            TallyByIDExceptMetadata: null,
            SubTallyByID: null,
            SubTallyByIDExceptMetadata:
              getNonMetaIds(requiredData?.SubtallyData) ?? '',
            SubTallyByType: null,
            TallyResultIDExceptMetadata:
              getResultLabelIds(
                requiredData?.SubtallyData?.idLabels,
                requiredData?.SubtallyResults?.docs
              ) ?? '',
            TallyResultID: null,
            SubTallyByResultIDExceptMetadata: null,
            TallyFilterValue: [],
            SubTallyFilterValue: [],
          };

    return {
      SourceType: selectedSource[0].sourceName,
      SourceID: selectedSource[0].source,
      DocListID: 0,
      Tally: tally,
      SubTally: subTally,
      DataAsOf:
        subtallyResult?.dataasof ??
        tallyResult?.dataasof ??
        '0001-01-01T00:00:00',
      IsDocumentCount: true,
      DocumentCount: 0,
      NavigationSourceType: selectedSource[0].navigationSourceType,
    };
  }, [selectedSource, tallyData, subtallyData, tallyResult, subtallyResult]);

  const getBulkFolderActionSourceDetails = (data: {
    operationType?: string;
    sourceDetails?: {[key: string]: any};
  }): {
    selectedList: {[key: string]: any};
    destinationList: {[key: string]: any};
  } => {
    const {sourceDetails = {}} = data;

    const updatedSourceDetails: {[key: string]: any} = {...sourceDetails};

    if (
      sourceDetails.SourceID &&
      Array.isArray(sourceDetails.SourceID) &&
      sourceDetails.SourceID.length > 0
    ) {
      updatedSourceDetails.SourceID = parseInt(sourceDetails.SourceID[0]);
    }
    return {selectedList: updatedSourceDetails, destinationList: {}};
  };

  const handleExport = useCallback(
    async (action: any, actionConfig: any) => {
      setShowLoaderOverlay(true);

      const showError = () =>
        triggerToast('Something went wrong.', [ToastType.Error]);

      const report = await handleReportDocId({
        sourceType,
        actionConfig,
        requiredData,
        getReportDocId,
      });

      if (!report.success || !report.data) {
        dispatch(clearSelectedAction());
        setShowLoaderOverlay(false);
        showError();
        return;
      }

      const docId = report.data?.docListId || 0;

      dispatch(
        updateTally({
          tallyExportData: {
            docId,
          },
        })
      );

      if (action === 'exportdata') {
        handleExportData(docId);
      } else {
        setShowLoaderOverlay(false);
      }
    },
    [requiredData, getReportDocId, triggerToast, sourceType]
  );
  const handleExportData = useCallback(
    async (docId: number) => {
      const showError = () =>
        triggerToast('Something went wrong.', [ToastType.Error]);

      const view = await handleExportView({docId, getExportView});

      if (!view?.success || !view?.data) {
        dispatch(clearSelectedAction());
        showError();
        return;
      }
      const masterDocIdString = docId.toString();
      setShowLoaderOverlay(false);
      navigate('/Report/ExportData', {
        state: {
          item: {
            navigationSourceType: SourceTypeCode.TALLY,
            source: masterDocIdString,
            sourceName: 'DOCLIST',
            sourceDisable: true,
          },
        },
      });
      dispatch(clearSelectedAction());
    },
    [getExportView, dispatch, navigate, triggerToast, setShowLoaderOverlay]
  );
  const documentViewerListPayload = useCallback(
    (
      totalDocumentToView: number,
      documentListMasterID: number
    ): DocumentViewerListIdRequest =>
      createDocumentViewerListRequestPayloadFromTally({
        sourceID: sourceId ?? 0,
        totalDocumentToView,
        documentListMasterID,
      }),
    [sourceId]
  );

  const onContinueDocLimit = useCallback(async () => {
    setShowLoaderOverlay(true);
    const action = String(actionMenuSelected?.value || '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '');

    setShowDocLimitDialog(false);
    fetchConfig();
    const showError = () =>
      triggerToast('Something went wrong.', [ToastType.Error]);

    const report = await handleReportDocId({
      sourceType,
      actionConfig,
      requiredData,
      getReportDocId,
    });
    if (!report.success || !report.data) {
      setShowLoaderOverlay(false);
      dispatch(clearSelectedAction());
      return showError();
    }
    const docId = report.data?.docListId || 0;
    const totalDocumentToView = report.data?.tallyFilter?.documentCount || 0;

    const docReport = await handleDocReport({
      sourceType,
      docListId: docId,
      totalDocumentToView: totalDocumentToView,
      actionConfig,
      requiredData,
      getDocReport,
    });

    if (!docReport.success || !docReport.data) {
      setShowLoaderOverlay(false);
      dispatch(clearSelectedAction());
      return showError();
    } else if (
      docReport.success &&
      docReport.data &&
      action === 'viewindoclist'
    ) {
      dispatch(setDocListId(docReport.data.docListID));
      dispatch(storeSourceTypeCode(SourceTypeCode.SUBTALLY));
      dispatch(storeSourceId(sourceId));
      const backPath = `${window.location.pathname}${window.location.search}`;
      dispatch(
        setDocListBackNavigation({
          label: t('reports.tally.title'),
          path: backPath,
        })
      );
      navigate('/Document/DocList');
      dispatch(clearSelectedAction());
      setShowLoaderOverlay(false);
    }

    action !== 'viewindoclist' &&
      fetchDocumentViewerListId(
        documentViewerListPayload(totalDocumentToView, docId),
        {
          onSuccess: (response) => {
            clearDocViewState();
            const masterId =
              response?.documentListID ?? response?.defaultMasterID ?? 0;
            switch (action) {
              case 'viewindocview': {
                dispatch(storeDocumentListMasterId(String(masterId)));
                dispatch(
                  storeSourceTypeCode(
                    sourceType == 'TALLY'
                      ? SourceTypeCode.TALLY
                      : SourceTypeCode.SUBTALLY
                  )
                );
                dispatch(storeSourceId(sourceId));
                navigate('/DocumentViewer/DocView');
                dispatch(clearSelectedAction());
                setShowLoaderOverlay(false);
                break;
              }
              case 'viewindocviewinnewtab': {
                const newWindow = window.open('about:blank', '_blank');
                if (newWindow) {
                  dispatch(storeDocumentListMasterId(String(masterId)));
                  dispatch(
                    storeSourceTypeCode(
                      sourceType == 'TALLY'
                        ? SourceTypeCode.TALLY
                        : SourceTypeCode.SUBTALLY
                    )
                  );
                  dispatch(storeSourceId(sourceId));
                  newWindow.location.href = `${window.location.origin}/DocumentViewer/DocView`;
                  dispatch(clearSelectedAction());
                  setShowLoaderOverlay(false);
                }
                break;
              }
              default: {
                dispatch(clearSelectedAction());
                setShowLoaderOverlay(false);

                break;
              }
            }
          },
          onError: (error) => {
            setShowLoaderOverlay(false);

            triggerToast(error?.message ?? '', [ToastType.Error]);
          },
        }
      );
  }, [
    getReportDocId,
    navigate,
    triggerToast,
    requiredData,
    actionMenuSelected,
    documentViewerListPayload,
    fetchDocumentViewerListId,
    getDocReport,
    actionConfig,
  ]);
  const onCancelDocLimit = useCallback(() => {
    setShowDocLimitDialog(false);
    dispatch(clearSelectedAction());
  }, []);

  const handleWrapperInteract = useCallback(() => {
    if (isDisabled) return;
    // Fire immediately when the user interacts with the TallyActions wrapper
    onActionClick?.();
  }, [isDisabled, onActionClick, sourceType]);

  return (
    <>
      <ArrLoadingSpinner
        show={showLoaderOverlay || isDocViewLoading}
        overlay={true}
        isOpen={true}
      />

      <div
        data-testid={`${dataTestId}-wrapper`}
        onMouseDownCapture={handleWrapperInteract}
      >
        <ActionMenuWrapper
          actions={actions}
          isDisabled={!!isDisabled}
          sourceType={SourceTypeCode.TALLY}
          dataTestId={dataTestId}
          direction={direction}
          size={size}
          subMenuDirection={subMenuDirection}
          sourceTypeContext={sourceTypeContext}
          sourceDetails={sourceDetails}
          getSourceDetails={(sourceDetails, forAPI, forAction) => {
            const updatedSourceDetails = _.cloneDeep(sourceDetails);
            if (
              forAPI == 'getBulkDocListId' &&
              [
                ReportActionMenuOptionTypes.BulkAssign,
                ReportActionMenuOptionTypes.QuickBatch,
              ].includes(forAction as ReportActionMenuOptionTypes)
            ) {
              return {};
            }
            return updatedSourceDetails;
          }}
          getDoActionData={getBulkFolderActionSourceDetails}
        />
      </div>
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={toastStatus}
          onClose={() => setShowToast(false)}
        />
      )}
      <ArrDocMaxLimitWarning
        totalDocumentCount={
          sourceType === 'TALLY'
            ? totalSelectedTallyBarDocs
            : totalSelectedSubTallyDocs
        }
        show={showDocLimitDialog}
        destination={docLimitSourceTypeCode}
        onContinue={onContinueDocLimit}
        onCancel={onCancelDocLimit}
      />
    </>
  );
};

export default TallyActions;
